import { Box, ModalBody, ModalContent, ModalFooter, Text, VStack } from "@chakra-ui/react";
import { type ChainFullId } from "@umami/chains";
import { useCurrentAccount, useGetSaplingAccount } from "@umami/state";
import { FormProvider } from "react-hook-form";

import { AmountField, MemoField, ShieldedAddressField, ToAddressField } from "./form";
import { useSendShieldedForm } from "./useSendShieldedForm";
import { FormPageHeader } from "../../SendFlow/FormPageHeader";

import { SignButton } from "@/components/SendFlow/SignButton";

type PrepareTransactionPageProps = {
  chainId: ChainFullId;
  isUnshield?: boolean;
};

export const PrepareTransactionPage = ({
  chainId,
  isUnshield = false,
}: PrepareTransactionPageProps) => {
  const currentAccount = useCurrentAccount();
  const saplingAccount = useGetSaplingAccount(currentAccount?.address.pkh ?? "");
  const shieldedBalance = saplingAccount?.balance ? Number(saplingAccount.balance) : 0;
  const toAddress = isUnshield ? currentAccount?.address.pkh : undefined;

  const {
    form,
    errors,
    register,
    handleSubmit,
    isLoading,
    isSaplingAddress,
    previewButtonText,
    validateAmount,
    validateAddress,
    symbol,
    shieldedAddress,
  } = useSendShieldedForm({ chainId, shieldedBalance, defaultToAddress: toAddress });

  return (
    <FormProvider {...form}>
      <ModalContent>
        <form>
          <FormPageHeader title={isUnshield ? "Unshield" : "Send Shielded"} />
          <ModalBody gap="24px">
            {currentAccount && shieldedAddress && (
              <ShieldedAddressField address={shieldedAddress} balance={shieldedBalance} />
            )}
            <AmountField
              errors={errors}
              isDisabled={isLoading}
              register={register}
              symbol={symbol}
              validateAmount={validateAmount}
            />
            {!isUnshield && (
              <ToAddressField
                errors={errors}
                isDisabled={isLoading}
                validateAddress={validateAddress}
              />
            )}
            {isSaplingAddress && (
              <MemoField errors={errors} isDisabled={isLoading} register={register} />
            )}
          </ModalBody>
          <ModalFooter>
            <Box width="full">
              <VStack alignItems="center">
                {isLoading && <Text>Building the transaction may take a minute or more.</Text>}
                {currentAccount && (
                  <SignButton
                    isLoading={isLoading}
                    onSubmit={(_tezosToolkit, options) =>
                      form.handleSubmit(values => handleSubmit(values, options))()
                    }
                    signer={currentAccount}
                    text={previewButtonText}
                  />
                )}
              </VStack>
            </Box>
          </ModalFooter>
        </form>
      </ModalContent>
    </FormProvider>
  );
};
